import * as React from 'react';

import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CircleX, LoaderIcon, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { TeamMemberIntegration } from '@/modules/team';
import { TeamMemberIntegrationsSelectCell } from '@/modules/team/components/member-details/integrations/team-member-integrations-select-cell';
import { SettingsRoutes } from '@/router/routes';

interface TeamMemberIntegrationsTableProps {
  integrations: TeamMemberIntegration[];
  isLoading: boolean;
  error?: string | null;
  isReadMode: boolean;
  onTeamMemberChange: (integrationId: string, memberId?: string) => void;
}

export const TeamMemberIntegrationsTable: React.FC<TeamMemberIntegrationsTableProps> = ({
  integrations,
  isLoading,
  error,
  isReadMode,
  onTeamMemberChange,
}) => {
  const { t } = useTranslation(['authenticated.team']);

  const columns: ColumnDef<TeamMemberIntegration>[] = React.useMemo(
    () => [
      {
        accessorKey: 'type',
        header: t('details.tabs.integrations.tableHeaders.integration'),
        cell: ({ row }) => {
          const integration = row.original;
          return (
            <div className="flex items-center gap-3">
              <EntityAvatar
                image={integration.icon}
                name={integration.type}
                size="sm"
                shape="rounded"
                className="flex-shrink-0"
                shouldDownscale={false}
              />
              <span className="font-medium">{integration.type}</span>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'selectedTeamMember',
        header: t('details.tabs.integrations.tableHeaders.memberName'),
        cell: ({ row }) => {
          const integration = row.original;
          return (
            <TeamMemberIntegrationsSelectCell
              integration={integration}
              onTeamMemberChange={onTeamMemberChange}
              isReadMode={isReadMode}
              t={t}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [t, isReadMode, onTeamMemberChange]
  );

  // Create React Table instance
  const table = useReactTable({
    data: integrations,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
  });

  /**
   * Renders the loading state when data is being fetched.
   * Shows a centered loading message within the table structure.
   */
  const renderLoadingState = (): React.ReactNode => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="h-32 px-4">
            <div className="flex w-full content-center justify-center">
              <LoaderIcon className="h-4 w-4 animate-spin text-foreground opacity-50" />
            </div>
          </td>
        </tr>
      );
    }
    return null;
  };

  /**
   * Renders the error state when data fetching fails.
   * Shows a centered error message within the table structure.
   */
  const renderErrorState = (): React.ReactNode => {
    if (error && !isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="h-32 px-4">
            <Alert variant="destructiveFilled">
              <div className="flex items-center gap-1.5">
                <CircleX className="h-4 w-4 flex-shrink-0" />
                <AlertDescription className="text-sm/4 font-medium">{error}</AlertDescription>
              </div>
            </Alert>
          </td>
        </tr>
      );
    }
    return null;
  };

  /**
   * Renders the empty state when no integrations are connected.
   * Shows a centered message with connect integrations button.
   */
  const renderEmptyState = (): React.ReactNode => {
    if (integrations.length === 0 && !isLoading && !error) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="p-4">
            <div className="relative box-border flex w-full flex-col items-start justify-center gap-3">
              <div className="flex w-full flex-col items-start justify-start gap-0.5">
                <div className="w-full text-sm font-semibold leading-5 text-foreground">
                  {t('details.tabs.integrations.emptyState.title')}
                </div>
                <div className="w-full text-xs font-normal leading-4 text-muted-foreground">
                  {t('details.tabs.integrations.emptyState.subtitle')}
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                className="h-9 gap-2 rounded-md px-3 py-2"
                onClick={() => {
                  window.open(SettingsRoutes.Integrations, '_blank');
                }}
              >
                <Settings className="h-4 w-4" />
                {t('details.tabs.integrations.emptyState.connectButton')}
              </Button>
            </div>
          </td>
        </tr>
      );
    }
    return null;
  };

  // Combine all conditional row elements for DataTable integration
  const lastRowChildren = (
    <>
      {renderLoadingState()}
      {renderErrorState()}
      {renderEmptyState()}
    </>
  );

  return <DataTable table={table} lastRowChildren={lastRowChildren} />;
};
